import { useState } from "react";
import { useLock } from "@/context/LockContext";
import { toast } from "sonner";
import { Lock, X, ShieldCheck } from "lucide-react";

export default function PinSettingsDialog() {
  const { settingsOpen, closeSettings, pinSet, savePin, removePin, lockNow } = useLock();
  const [pin, setPin] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  if (!settingsOpen) return null;

  const clean = (v) => v.replace(/\D/g, "").slice(0, 8);

  const reset = () => {
    setPin("");
    setConfirm("");
  };

  const save = async () => {
    if (pin.length < 4) return toast.error("Choose at least 4 digits.");
    if (pin !== confirm) return toast.error("The passcodes don't match.");
    setBusy(true);
    await savePin(pin);
    setBusy(false);
    reset();
    toast.success(pinSet ? "Passcode updated." : "Passcode set — your space is protected.");
    closeSettings();
  };

  const remove = () => {
    removePin();
    reset();
    toast.success("Passcode removed.");
    closeSettings();
  };

  const lock = () => {
    closeSettings();
    lockNow();
  };

  return (
    <div
      data-testid="pin-settings"
      className="fixed inset-0 z-[150] grid place-items-center px-6"
      style={{ background: "rgba(20,14,10,0.82)", backdropFilter: "blur(6px)" }}
      onClick={closeSettings}
    >
      <div className="relative glass rounded-3xl p-8 w-full max-w-md border border-[#D4AF37]/25" onClick={(e) => e.stopPropagation()}>
        <button data-testid="pin-settings-close" onClick={closeSettings} className="absolute top-4 right-4 text-[#B4A897] hover:text-[#F7F1E6] transition-colors duration-300">
          <X size={20} />
        </button>
        <div className="flex items-center gap-3">
          <span className="h-11 w-11 rounded-full bg-[#D4AF37]/10 grid place-items-center text-[#D4AF37]">
            <Lock size={20} />
          </span>
          <div>
            <h2 className="serif text-2xl text-[#F7F1E6]">Privacy Lock</h2>
            <p className="text-xs text-[#B4A897]">A passcode kept on this device only.</p>
          </div>
        </div>

        <div className="mt-7 space-y-4">
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-[#B4A897]">{pinSet ? "New passcode" : "Choose a passcode"}</label>
            <input
              data-testid="pin-new-input"
              type="password"
              inputMode="numeric"
              value={pin}
              onChange={(e) => setPin(clean(e.target.value))}
              placeholder="4–8 digits"
              className="mt-2 w-full bg-white/[0.04] border border-white/10 rounded-xl px-4 py-3 text-[#F7F1E6] tracking-[0.3em] outline-none focus:border-[#D4AF37]/60 transition-colors duration-300"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-[#B4A897]">Confirm passcode</label>
            <input
              data-testid="pin-confirm-input"
              type="password"
              inputMode="numeric"
              value={confirm}
              onChange={(e) => setConfirm(clean(e.target.value))}
              placeholder="Repeat it"
              className="mt-2 w-full bg-white/[0.04] border border-white/10 rounded-xl px-4 py-3 text-[#F7F1E6] tracking-[0.3em] outline-none focus:border-[#D4AF37]/60 transition-colors duration-300"
            />
          </div>
        </div>

        <button
          data-testid="pin-save"
          onClick={save}
          disabled={busy}
          className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#D4AF37] text-[#17100C] px-6 py-3 font-medium hover:bg-[#e6c860] transition-colors duration-300 disabled:opacity-50"
        >
          <ShieldCheck size={17} /> {pinSet ? "Update passcode" : "Set passcode"}
        </button>

        {pinSet && (
          <div className="mt-4 flex items-center justify-between gap-3">
            <button data-testid="pin-lock-now" onClick={lock} className="text-sm text-[#D4AF37] hover:underline">
              Lock now
            </button>
            <button data-testid="pin-remove" onClick={remove} className="text-sm text-[#B4A897] hover:text-[#C1272D] transition-colors duration-300">
              Remove passcode
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
